<div class="marketing-tab">
  <div class="toolbar">
    <div>
      <h3 class="section-title">History</h3>
      <div class="subtitle">Recent campaign delivery attempts and recipient outcomes.</div>
    </div>
    <app-paginator [reload]="reload" [itemCount]="itemCount" (pageEvent)="pageEvent($event)"></app-paginator>
  </div>

  <div class="filters">
    <mat-form-field appearance="fill">
      <mat-label>Status</mat-label>
      <mat-select [value]="status" (selectionChange)="setStatusFilter($event)">
        <mat-option value="all">All</mat-option>
        @for (value of filterValues; track value) {
          <mat-option [value]="value">
            {{ utilService.capitalize(value) }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="fill">
      <mat-label>Campaign</mat-label>
      <mat-select [value]="campaignId" (selectionChange)="setCampaignFilter($event)">
        <mat-option value="all">All</mat-option>
        @for (campaign of campaigns; track campaign) {
          <mat-option [value]="campaign.id">{{ campaign.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="fill">
      <mat-label>Channel</mat-label>
      <mat-select [value]="channel" (selectionChange)="setChannelFilter($event)">
        <mat-option value="all">All</mat-option>
        <mat-option value="email">Email</mat-option>
        <mat-option value="sms">SMS</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="fill" class="search">
      <mat-label>Recipient search</mat-label>
      <input #search matInput placeholder="Email address or target" [formControl]="searchForm" />
      <button mat-icon-button matSuffix type="button" aria-label="Search history" (click)="load()">
        <mat-icon>search</mat-icon>
      </button>
    </mat-form-field>
  </div>

  <div class="summary-grid">
    <div class="summary-item">
      <span class="label">Sent</span>
      <strong>{{ analyticsTotals.sentCount }}</strong>
    </div>
    <div class="summary-item">
      <span class="label">Delivered</span>
      <strong>{{ analyticsTotals.deliveredCount }}</strong>
    </div>
    <div class="summary-item">
      <span class="label">Failed</span>
      <strong>{{ analyticsTotals.failedCount }}</strong>
    </div>
    <div class="summary-item">
      <span class="label">Delivery rate</span>
      <strong>{{ formatRate(analyticsTotals.deliveryRate) }}</strong>
    </div>
    <div class="summary-item">
      <span class="label">Unique clicks</span>
      <strong>{{ analyticsTotals.uniqueClickedCount }}</strong>
    </div>
    <div class="summary-item">
      <span class="label">Click rate</span>
      <strong>{{ formatRate(analyticsTotals.uniqueClickRate) }}</strong>
    </div>
    <div class="summary-item">
      <span class="label">Total clicks</span>
      <strong>{{ analyticsTotals.totalClickCount }}</strong>
    </div>
  </div>

  <div class="analytics-note">
    Click rate counts messages with at least one tracked-link click. SMS does not provide an open or read rate.
  </div>
  @if (analyticsError) {
    <div class="analytics-error">{{ analyticsError }}</div>
  }

  @if (analytics.length > 0) {
    <div class="analytics-table-wrap">
      <table
        class="analytics-table"
        mat-table
        matSort
        #analyticsSort="matSort"
        [dataSource]="analyticsDataSource"
        >
        <ng-container matColumnDef="campaignName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Campaign</th>
          <td mat-cell *matCellDef="let item">
            <button type="button" class="analytics-campaign-link" (click)="selectCampaign(item.campaignId)">
              {{ getCampaignName(item.campaignId) }}
            </button>
          </td>
        </ng-container>
        <ng-container matColumnDef="channel">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Channel</th>
          <td mat-cell *matCellDef="let item">{{ getChannelLabel(item.channel) }}</td>
        </ng-container>
        <ng-container matColumnDef="initialTimestamp">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Initial timestamp</th>
          <td mat-cell *matCellDef="let item">{{ item.initialTimestamp | date : 'medium' }}</td>
        </ng-container>
        <ng-container matColumnDef="sentCount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Sent</th>
          <td mat-cell *matCellDef="let item">{{ item.sentCount }}</td>
        </ng-container>
        <ng-container matColumnDef="deliveredCount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Delivered</th>
          <td mat-cell *matCellDef="let item">{{ item.deliveredCount }}</td>
        </ng-container>
        <ng-container matColumnDef="failedCount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Failed</th>
          <td mat-cell *matCellDef="let item">{{ item.failedCount }}</td>
        </ng-container>
        <ng-container matColumnDef="deliveryRate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Delivery rate</th>
          <td mat-cell *matCellDef="let item">{{ formatRate(item.deliveryRate) }}</td>
        </ng-container>
        <ng-container matColumnDef="uniqueClickedCount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Unique clicks</th>
          <td mat-cell *matCellDef="let item">{{ item.uniqueClickedCount }}</td>
        </ng-container>
        <ng-container matColumnDef="totalClickCount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Total clicks</th>
          <td mat-cell *matCellDef="let item">{{ item.totalClickCount }}</td>
        </ng-container>
        <ng-container matColumnDef="uniqueClickRate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Click rate</th>
          <td mat-cell *matCellDef="let item">{{ formatRate(item.uniqueClickRate) }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="analyticsColumnNames"></tr>
        <tr mat-row *matRowDef="let row; columns: analyticsColumnNames"></tr>
      </table>
    </div>
  }

  <div class="recipient-history-heading">
    <div>
      <h4>Recipient history</h4>
      <span>{{ itemCount }} result{{ itemCount === 1 ? '' : 's' }}</span>
    </div>
  </div>

  <div class="table-wrap">
    <table class="table" mat-table matSort #historySort="matSort" matSortActive="sentAt" matSortDirection="desc" [dataSource]="tableDataSource">
      <ng-container matColumnDef="campaignName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Campaign</th>
        <td class="cell" mat-cell *matCellDef="let item">
          <div class="cell-primary">{{ item.campaign.name || 'Unknown campaign' }}</div>
          <div class="cell-secondary">{{ item.campaign.id || '-' }}</div>
        </td>
      </ng-container>
      <ng-container matColumnDef="target">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Target</th>
        <td class="cell" mat-cell *matCellDef="let item">{{ item.target }}</td>
      </ng-container>
      <ng-container matColumnDef="channel">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Channel</th>
        <td class="cell" mat-cell *matCellDef="let item">
          <span class="type-pill">{{ getChannelLabel(item.channel) }}</span>
        </td>
      </ng-container>
      <ng-container matColumnDef="sentAt">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Sent At</th>
        <td class="cell" mat-cell *matCellDef="let item">{{ item.sentAt ? (item.sentAt | date : 'medium') : '—' }}</td>
      </ng-container>
      <ng-container matColumnDef="clickCount">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Clicks</th>
        <td class="cell" mat-cell *matCellDef="let item">
          <span class="count-pill">{{ item.clickCount == null ? '-' : item.clickCount }}</span>
        </td>
      </ng-container>
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
        <td class="cell" mat-cell *matCellDef="let item">
          <span class="status-pill" [ngClass]="getStatusClass(item.status)">
            {{ utilService.capitalize(item.status) }}
          </span>
          @if (item.error) {
            <div class="cell-secondary delivery-error">{{ item.error }}</div>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="actionmenu" stickyEnd>
        <th mat-header-cell matTooltip="Actions" *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let item">
          <button
            mat-icon-button
            class="action-button"
            [matMenuTriggerFor]="menu"
            [matMenuTriggerData]="{ item: item }"
            aria-label="History item actions"
            (click)="$event.stopPropagation()"
            >
            <mat-icon matTooltip="Actions" class="more">more_vert</mat-icon>
          </button>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="columnNames"></tr>
      <tr class="listitem" mat-row *matRowDef="let rows; columns: columnNames"></tr>
    </table>
    @if (tableDataSource.data.length === 0) {
      <div class="empty">
        No campaign history matches the current filters.
      </div>
    }
    <mat-menu #menu="matMenu">
      <ng-template matMenuContent let-item="item">
        <button mat-menu-item (click)="showDetails(item)">
          <mat-icon>info</mat-icon>
          Details
        </button>
      </ng-template>
    </mat-menu>
  </div>
</div>
